import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {testIdProps} from '@github-ui/test-id-props'
import {useMemo} from 'react'

import {VALUES} from './constants/values'
import {MilestoneEmptyState} from './MilestoneEmptyState'
import {MilestoneListPagination} from './MilestoneListPagination'
import {MilestoneRow} from './MilestoneRow'
import {MilestoneSortMenu} from './MilestoneSortMenu'
import {OpenClosedMilestones} from './OpenClosedMilestones'
import type {RepositoryResult} from './repository-milestone-index-page-query-types'
import styles from './RepositoryMilestone.module.css'

// RELAY-CLEANUP(RepositoryMilestoneIndexPageContentInternal): widens nullable fields so the still-Relay parent
// data is assignable. Remove when RepositoryMilestoneIndexPageContentInternal leaves Relay.
export type MilestoneListFragment = RelayCompat<
  Pick<RepositoryResult, 'nameWithOwner' | 'viewerCanPush' | 'milestones' | 'open' | 'closed'>
>

type MilestoneListProps = {
  repository: MilestoneListFragment
}

export function MilestoneList({repository}: MilestoneListProps) {
  if (!repository.milestones) {
    throw new Error('Milestones data is null or undefined')
  }

  const nodes = useMemo(() => {
    return (
      repository.milestones?.edges
        ?.map(edge => edge?.node)
        .filter((node): node is NonNullable<typeof node> => node != null) ?? []
    )
  }, [repository])

  const actions = useMemo(() => {
    return [
      {
        key: 'milestone-sort-menu',
        render: () => <MilestoneSortMenu />,
      },
    ]
  }, [])

  const rootUrl = `${ssrSafeLocation.origin}/${repository.nameWithOwner}`
  const newMilestoneUrl = `${rootUrl}/milestones/new`

  const listItemsHeader = (
    <ListViewMetadata
      actions={actions}
      actionsLabel="Actions"
      sectionFilters={<OpenClosedMilestones repository={repository} />}
    />
  )

  const renderMilestoneList = () => {
    if (nodes.length > 0) {
      return nodes.map(node => (
        <MilestoneRow key={node.id} milestone={node} repositoryNameWithOwner={repository.nameWithOwner} />
      ))
    }

    const openCount = repository.open?.totalCount ?? 0
    const closedCount = repository.closed?.totalCount ?? 0
    const noCreatedMilestones = openCount === 0 && closedCount === 0

    return (
      <MilestoneEmptyState
        noCreatedMilestones={noCreatedMilestones}
        newMilestoneUrl={newMilestoneUrl}
        viewerCanPush={repository.viewerCanPush}
      />
    )
  }

  return (
    <>
      <div className={styles.milestoneListWrapper} data-hpc>
        <ListView
          {...testIdProps('repository-milestone-list-view')}
          title=""
          metadata={listItemsHeader}
          titleHeaderTag="h2"
          singularUnits={'milestone'}
          pluralUnits={'milestones'}
          as={nodes.length > 0 ? 'ul' : 'div'}
          role={nodes.length > 0 ? 'list' : 'status'}
        >
          {renderMilestoneList()}
        </ListView>
      </div>
      {(repository.milestones?.totalCount ?? 0) > VALUES.milestonePageSize && (
        <MilestoneListPagination dataCount={repository.milestones?.totalCount ?? 0} />
      )}
    </>
  )
}
